import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";

export const CartContext = createContext({});

const readStoredValue = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const storeValue = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The storefront remains usable when storage is unavailable.
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => readStoredValue("miskergarden-cart", []));
  const [wishlist, setWishlist] = useState(() => readStoredValue("miskergarden-wishlist", []));

  useEffect(() => {
    storeValue("miskergarden-cart", cartItems);
  }, [cartItems]);

  useEffect(() => {
    storeValue("miskergarden-wishlist", wishlist);
  }, [wishlist]);

  const addToCart = useCallback((productId, quantity = 1, size = "", color = "") => {
    const safeQuantity = Math.max(1, Number.parseInt(quantity, 10) || 1);
    setCartItems((current) => {
      const index = current.findIndex(
        (item) => item.id === productId && item.size === size && item.color === color
      );
      if (index < 0) return [...current, { id: productId, size, color, quantity: safeQuantity }];
      return current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, quantity: item.quantity + safeQuantity } : item
      );
    });
  }, []);

  const removeFromCart = useCallback((productId, size = "", color = "") => {
    setCartItems((current) =>
      current.filter(
        (item) => !(item.id === productId && item.size === size && item.color === color)
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, size, color, quantity) => {
    const safeQuantity = Math.max(1, Number.parseInt(quantity, 10) || 1);
    setCartItems((current) =>
      current.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: safeQuantity }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);
  const addToWishlist = useCallback(
    (productId) => setWishlist((current) => (current.includes(productId) ? current : [...current, productId])),
    []
  );
  const removeFromWishlist = useCallback(
    (productId) => setWishlist((current) => current.filter((id) => id !== productId)),
    []
  );
  const toggleWishlist = useCallback(
    (productId) =>
      setWishlist((current) =>
        current.includes(productId)
          ? current.filter((id) => id !== productId)
          : [...current, productId]
      ),
    []
  );

  const value = useMemo(
    () => ({
      cartItems,
      wishlist,
      cartCount: cartItems.reduce((sum, item) => sum + item.quantity, 0),
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted: (productId) => wishlist.includes(productId),
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlist,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
